import { useState, type ReactNode } from 'react';
import type {
  CharityContactConfirmationMethod,
  CharityDocumentDecision,
  CharityIndependentSourceType,
  CharityLetterKind,
  CharityRejectionReasonCode,
  CharityRubricGate,
  CharityRubricResult,
  CharityVerificationDecisionPayload,
  CharityVerificationDetail,
} from '../api/adminOps';
import {
  ContactMethod,
  ContactState,
  Decision,
  DocumentDecision,
  type Option,
} from './charityVerification.logic';
import {
  buildDecisionPayload,
  CONTACT_METHOD_OPTIONS,
  decisionLabel,
  decisionProblems,
  decisionRecordsEvidence,
  DOCUMENT_DECISION_OPTIONS,
  EMPTY_DECISION_DRAFT,
  EMPTY_DOCUMENT_DRAFT,
  GATE_RESULT_OPTIONS,
  INDEPENDENT_SOURCE_OPTIONS,
  isResubmitLocked,
  LETTER_KIND_OPTIONS,
  NOTE_MAX_LENGTH,
  REJECTION_REASON_OPTIONS,
  resubmitAllowedFor,
  RUBRIC_RESULT_OPTIONS,
  visibleDecisionOptions,
  type DecisionDraft,
  type DocumentDecisionDraft,
} from './charityVerificationDecision.logic';
import { SECTION_HEADING } from './CharityVerificationCaseView';

const ID_PREFIX = 'charity-decision';

export const CHOOSE_DECISION_TEXT = 'Choose a decision';
export const SUBMITTING_TEXT = 'Submitting…';
export const RESUBMIT_LOCKED_TEXT =
  'Not eligible never allows a resubmission; the claimant is told the organization cannot be verified.';

interface SelectFieldProps<T extends string> {
  id: string;
  label: string;
  value: T | '';
  options: readonly Option<T>[];
  placeholder: string;
  onChange: (value: T | '') => void;
}

function SelectField<T extends string>({
  id,
  label,
  value,
  options,
  placeholder,
  onChange,
}: SelectFieldProps<T>) {
  return (
    <div>
      <label className="label" htmlFor={`${ID_PREFIX}-${id}`}>
        {label}
      </label>
      <select
        id={`${ID_PREFIX}-${id}`}
        className="input w-full"
        value={value}
        onChange={event => onChange(event.target.value as T | '')}
      >
        <option value="">{placeholder}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'url';
}) {
  return (
    <div>
      <label className="label" htmlFor={`${ID_PREFIX}-${id}`}>
        {label}
      </label>
      <input
        id={`${ID_PREFIX}-${id}`}
        className="input w-full"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(event.target.value)}
      />
    </div>
  );
}

function Checkbox({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-white" htmlFor={`${ID_PREFIX}-${id}`}>
      <input
        id={`${ID_PREFIX}-${id}`}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-3 rounded border border-gray-border p-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-gray-text/70">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

interface CharityVerificationDecisionFormProps {
  detail: CharityVerificationDetail;
  isPending?: boolean;
  onSubmit: (payload: CharityVerificationDecisionPayload) => void;
}

/**
 * DM-7976: one decision on one claim. Keyed on the claim by its parent, so a
 * note typed for one claim is never submitted against the next one opened.
 * The decision buttons follow the claim's allowed_actions; everything the
 * route would answer 422 for is checked here first (see decisionProblems).
 */
export default function CharityVerificationDecisionForm({
  detail,
  isPending,
  onSubmit,
}: CharityVerificationDecisionFormProps) {
  const [draft, setDraft] = useState<DecisionDraft>(EMPTY_DECISION_DRAFT);
  const update = (patch: Partial<DecisionDraft>) => setDraft(current => ({ ...current, ...patch }));
  const updateDocument = (documentId: string, patch: Partial<DocumentDecisionDraft>) =>
    setDraft(current => {
      const existing = current.documents[documentId] as DocumentDecisionDraft | undefined;
      return {
        ...current,
        documents: {
          ...current.documents,
          [documentId]: { ...(existing ?? EMPTY_DOCUMENT_DRAFT), ...patch },
        },
      };
    });

  const problems = decisionProblems(draft, {
    allowedActions: detail.allowed_actions,
    contactConfirmed: detail.contact_confirmation.state === ContactState.CONFIRMED,
  });
  const canSubmit = problems.length === 0 && !isPending;
  const options = visibleDecisionOptions(detail.allowed_actions);
  const selected = options.find(option => option.value === draft.decision);
  const recordsEvidence = decisionRecordsEvidence(draft.decision);
  const resubmitLocked = isResubmitLocked(draft.reasonCode);

  return (
    <section aria-labelledby="charity-decision" className="space-y-4">
      <h3 id="charity-decision" className={SECTION_HEADING}>
        Decision
      </h3>

      <div role="group" aria-label="Decision" className="flex flex-wrap gap-2">
        {options.map(option => {
          const allowed = detail.allowed_actions.includes(option.value);
          const active = draft.decision === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              disabled={!allowed}
              title={allowed ? option.hint : 'Not allowed in the claim’s current status'}
              onClick={() => update({ decision: option.value })}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                active ? 'bg-primary text-white' : 'bg-gray-500/10 text-gray-text hover:text-white'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {selected !== undefined && <p className="text-xs text-gray-text">{selected.hint}</p>}

      {draft.decision === Decision.REJECT && (
        <Group title="Rejection">
          <SelectField<CharityRejectionReasonCode>
            id="reason-code"
            label="Rejection reason"
            value={draft.reasonCode}
            options={REJECTION_REASON_OPTIONS}
            placeholder="Select a reason code…"
            onChange={reasonCode => update({ reasonCode })}
          />
          <Checkbox
            id="resubmit-allowed"
            label="Allow resubmission (14 days)"
            checked={resubmitAllowedFor(draft)}
            disabled={resubmitLocked}
            onChange={resubmitAllowed => update({ resubmitAllowed })}
          />
          {resubmitLocked && <p className="text-xs text-gray-text">{RESUBMIT_LOCKED_TEXT}</p>}
        </Group>
      )}

      {draft.decision === Decision.REQUEST_CONFIRMATION && (
        <Group title="Contact">
          <SelectField<CharityContactConfirmationMethod>
            id="contact-method"
            label="Contact method"
            value={draft.contactMethod}
            options={CONTACT_METHOD_OPTIONS}
            placeholder="Select how the organization is contacted…"
            onChange={contactMethod => update({ contactMethod })}
          />
          {draft.contactMethod === ContactMethod.CODE && (
            <TextField
              id="contact-email"
              label="Contact email"
              type="email"
              value={draft.contactEmail}
              placeholder="An address found independently of the claimant"
              onChange={contactEmail => update({ contactEmail })}
            />
          )}
          {draft.contactMethod === ContactMethod.PHONE && (
            <TextField
              id="contact-phone"
              label="Contact phone"
              type="tel"
              value={draft.contactPhone}
              placeholder="The number that was called"
              onChange={contactPhone => update({ contactPhone })}
            />
          )}
          {(draft.contactMethod === ContactMethod.PHONE ||
            draft.contactMethod === ContactMethod.LETTER_OF_AUTHORIZATION) && (
            <TextField
              id="contact-note"
              label="Contact note"
              value={draft.contactNote}
              placeholder="Who confirmed, when, and how"
              onChange={contactNote => update({ contactNote })}
            />
          )}
        </Group>
      )}

      {recordsEvidence && (
        <Group title="Rubric">
          <SelectField<CharityRubricGate>
            id="bmf-gate"
            label="BMF gate"
            value={draft.bmfGate}
            options={GATE_RESULT_OPTIONS}
            placeholder="Pass or fail…"
            onChange={bmfGate => update({ bmfGate })}
          />
          <SelectField<CharityRubricResult>
            id="letter-check"
            label="Letter check"
            value={draft.letterCheck}
            options={RUBRIC_RESULT_OPTIONS}
            placeholder="Pass, fail or not applicable…"
            onChange={letterCheck => update({ letterCheck })}
          />
          <TextField
            id="letter-check-notes"
            label="Letter check notes"
            value={draft.letterCheckNotes}
            onChange={letterCheckNotes => update({ letterCheckNotes })}
          />
          <SelectField<CharityIndependentSourceType>
            id="independent-source-type"
            label="Independent source type"
            value={draft.independentSourceType}
            options={INDEPENDENT_SOURCE_OPTIONS}
            placeholder="None recorded"
            onChange={independentSourceType => update({ independentSourceType })}
          />
          {draft.independentSourceType !== '' && (
            <>
              <TextField
                id="independent-source-value"
                label="Independent source value"
                value={draft.independentSourceValue}
                placeholder="A URL or registry reference"
                onChange={independentSourceValue => update({ independentSourceValue })}
              />
              <Checkbox
                id="matches-email-domain"
                label="Matches the claimant’s email domain"
                checked={draft.matchesEmailDomain}
                onChange={matchesEmailDomain => update({ matchesEmailDomain })}
              />
            </>
          )}
          <SelectField<CharityRubricResult>
            id="sanctions"
            label="Sanctions screening"
            value={draft.sanctions}
            options={RUBRIC_RESULT_OPTIONS}
            placeholder="Pass, fail or not applicable…"
            onChange={sanctions => update({ sanctions })}
          />
          <TextField
            id="sanctions-notes"
            label="Sanctions notes"
            value={draft.sanctionsNotes}
            onChange={sanctionsNotes => update({ sanctionsNotes })}
          />
          <SelectField<CharityRubricGate>
            id="name-comparison"
            label="Name comparison"
            value={draft.nameComparison}
            options={GATE_RESULT_OPTIONS}
            placeholder="Pass or fail…"
            onChange={nameComparison => update({ nameComparison })}
          />
          <TextField
            id="name-comparison-notes"
            label="Name comparison notes"
            value={draft.nameComparisonNotes}
            onChange={nameComparisonNotes => update({ nameComparisonNotes })}
          />
        </Group>
      )}

      {recordsEvidence && detail.documents.length > 0 && (
        <Group title="Documents">
          {detail.documents.map(document => {
            const entry = draft.documents[document.id];
            return (
              <div key={document.id} className="space-y-2">
                <SelectField<CharityDocumentDecision>
                  id={`document-${document.id}`}
                  label={`Document decision for ${document.filename}`}
                  value={entry?.status ?? ''}
                  options={DOCUMENT_DECISION_OPTIONS}
                  placeholder="No change"
                  onChange={status => updateDocument(document.id, { status })}
                />
                {entry?.status === DocumentDecision.REJECTED && (
                  <TextField
                    id={`document-${document.id}-reason`}
                    label={`Rejection reason for ${document.filename}`}
                    value={entry.rejectionReason}
                    placeholder="Told to the claimant"
                    onChange={rejectionReason => updateDocument(document.id, { rejectionReason })}
                  />
                )}
              </div>
            );
          })}
          <SelectField<CharityLetterKind>
            id="letter-kind"
            label="Letter kind"
            value={draft.letterKind}
            options={LETTER_KIND_OPTIONS}
            placeholder="Leave as extracted"
            onChange={letterKind => update({ letterKind })}
          />
        </Group>
      )}

      {recordsEvidence && (
        <TextField
          id="teos-url"
          label="TEOS link (https)"
          type="url"
          value={draft.teosUrl}
          placeholder="https://apps.irs.gov/app/eos/…"
          onChange={teosUrl => update({ teosUrl })}
        />
      )}

      <div>
        <label className="label" htmlFor={`${ID_PREFIX}-note`}>
          Note (required)
        </label>
        <textarea
          id={`${ID_PREFIX}-note`}
          className="input w-full min-h-[80px]"
          maxLength={NOTE_MAX_LENGTH}
          required
          placeholder="What you concluded and why — kept with the decision; for a rejection, shown to the claimant"
          value={draft.note}
          onChange={event => update({ note: event.target.value })}
        />
        <p className="text-xs text-gray-text mt-1 text-right">
          {draft.note.length}/{NOTE_MAX_LENGTH}
        </p>
      </div>

      {problems.length > 0 && (
        <ul aria-live="polite" className="list-disc pl-5 text-xs text-yellow-400 space-y-1">
          {problems.map(problem => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={!canSubmit}
        onClick={() => {
          if (canSubmit) onSubmit(buildDecisionPayload(draft));
        }}
      >
        {isPending
          ? SUBMITTING_TEXT
          : draft.decision === ''
            ? CHOOSE_DECISION_TEXT
            : `Record ${decisionLabel(draft.decision)}`}
      </button>
    </section>
  );
}
